import type { ApiErrorBody, FieldErrorDetail, OvertimeValidationResponse } from '@i9portal/shared';

export const NETWORK_ERROR_CODE = 'NETWORK_ERROR';
export const UNKNOWN_ERROR_CODE = 'UNKNOWN_ERROR';

/** Erro normalizado de qualquer chamada à API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly correlationId: string | null;

  constructor(params: {
    status: number;
    code: string;
    message: string;
    details?: unknown;
    correlationId?: string | null;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code;
    this.details = params.details;
    this.correlationId = params.correlationId ?? null;
  }

  /** Erros "inesperados" (5xx, rede, formato desconhecido) exibem o código para suporte. */
  get isUnexpected(): boolean {
    return this.status === 0 || this.status >= 500 || this.code === UNKNOWN_ERROR_CODE;
  }

  /** `details` de VALIDATION_ERROR: lista de { path, message }. */
  get fieldErrors(): FieldErrorDetail[] {
    if (this.code !== 'VALIDATION_ERROR' || !Array.isArray(this.details)) return [];
    return this.details.filter(
      (d): d is FieldErrorDetail =>
        typeof d === 'object' &&
        d !== null &&
        typeof (d as FieldErrorDetail).path === 'string' &&
        typeof (d as FieldErrorDetail).message === 'string',
    );
  }

  /** `details` de OVERTIME_INVALID: resultado da validação de jornada. */
  get overtimeValidation(): OvertimeValidationResponse | null {
    if (this.code !== 'OVERTIME_INVALID') return null;
    const d = this.details as Partial<OvertimeValidationResponse> | null | undefined;
    if (!d || typeof d !== 'object') return null;
    return {
      valid: false,
      durationMinutes: d.durationMinutes ?? 0,
      errors: Array.isArray(d.errors) ? d.errors : [],
      conflicts: Array.isArray(d.conflicts) ? d.conflicts : [],
      startsOnNonWorkingDay: d.startsOnNonWorkingDay ?? false,
      multiplierBps: d.multiplierBps ?? 0,
      estimatedAmountCents: d.estimatedAmountCents ?? null,
    };
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (!value || typeof value !== 'object') return false;
  const err = (value as { error?: unknown }).error;
  return (
    !!err &&
    typeof err === 'object' &&
    typeof (err as { code?: unknown }).code === 'string' &&
    typeof (err as { message?: unknown }).message === 'string'
  );
}

/** Converte qualquer erro em ApiError (útil para exibição). */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ApiError({ status: 0, code: 'ABORTED', message: 'Operação cancelada.' });
  }
  return new ApiError({
    status: 0,
    code: UNKNOWN_ERROR_CODE,
    message:
      error instanceof Error && error.message ? error.message : 'Ocorreu um erro inesperado.',
  });
}
